import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import Select from 'react-select-legacy';
import moment from 'moment';
import { authHeaders } from '../../actions';
import { debounce } from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';

const ASQ_TYPE_OPTIONS = [
  { value: 'asq_3', label: 'ASQ®-3' },
  { value: 'asq_se2', label: 'ASQ®:SE-2' },
];

const RESPONDENT_TYPE_OPTIONS = [
  { value: 'Account', label: 'Parent' },
  { value: 'AdminUser', label: 'Teacher' },
];

const postCompletedASQ = async (playerId, bodyParams) => {
  const response = await axios.post(
    `/api/dashboard/players/${playerId}/player_questionnaires/completed`,
    bodyParams,
    { headers: authHeaders() }
  );
  return response.data;
};

const fetchDateRange = async (playerId, asqType) => {
  const response = await axios.get(
    `/api/dashboard/players/${playerId}/player_questionnaires/valid_date_range?asq_type=${asqType}`,
    { headers: authHeaders() }
  );
  return response.data;
};

const fetchAgeRangeForDate = async (playerId, asqType, date) => {
  const dateStr = moment(date).format('YYYY-MM-DD');
  const response = await axios.get(
    `/api/dashboard/players/${playerId}/player_questionnaires/age_range_for_date?asq_type=${asqType}&administration_date=${dateStr}`,
    { headers: authHeaders() }
  );
  return response.data;
};

const fetchPlayers = async (search = '') => {
  const params = search ? `?q=${encodeURIComponent(search)}` : '';
  const response = await axios.get(
    `/api/dashboard/players_filters${params}`,
    { headers: authHeaders() }
  );
  return (response.data.players || []).map((p) => ({
    value: p.value,
    label: p.label,
    birthday: p.birthday,
    weeksPremature: p.weeks_premature,
  }));
};

const formatBirthday = (birthday) => {
  if (!birthday) return null;
  return moment(birthday, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('MM/DD/YYYY');
};

const AddCompletedASQModal = ({ playerId, player, onClose, onSuccess }) => {
  const preselectedPlayer = player
    ? { value: player.id, label: player.name, birthday: player.birthday, weeksPremature: player.weeksPremature }
    : null;
  const [selectedPlayer, setSelectedPlayer] = useState(preselectedPlayer);
  const [respondentType, setRespondentType] = useState('Account');
  const [asqType, setAsqType] = useState(null);
  const [administrationDate, setAdministrationDate] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');

  const activePlayerId = selectedPlayer?.value;
  const playerBirthday = selectedPlayer?.birthday;
  const playerWeeksPremature = selectedPlayer?.weeksPremature;

  const { data: playerOptions = [], isLoading: isLoadingPlayers } = useQuery({
    queryKey: ['allPlayers', playerSearch],
    queryFn: () => fetchPlayers(playerSearch),
    enabled: !preselectedPlayer,
  });

  const onPlayerInputChange = useCallback(
    debounce((value) => setPlayerSearch(value), 300),
    []
  );

  const { data: dateRange } = useQuery({
    queryKey: ['asqDateRange', activePlayerId, asqType],
    queryFn: () => fetchDateRange(activePlayerId, asqType),
    enabled: !!activePlayerId && !!asqType,
  });

  const { data: ageRangeData } = useQuery({
    queryKey: ['asqAgeRange', activePlayerId, asqType, administrationDate],
    queryFn: () => fetchAgeRangeForDate(activePlayerId, asqType, administrationDate),
    enabled: !!activePlayerId && !!asqType && !!administrationDate,
  });

  const minDate = dateRange ? moment(dateRange.min_date, 'MM/DD/YYYY').toDate() : undefined;
  const maxDate = dateRange ? moment(dateRange.max_date, 'MM/DD/YYYY').toDate() : undefined;
  const datePickerDisabled = !activePlayerId || !respondentType || !asqType;
  const noDateRange = !datePickerDisabled && dateRange && !dateRange.min_date && !dateRange.max_date;

  const mutation = useMutation({
    mutationFn: (bodyParams) => postCompletedASQ(activePlayerId, bodyParams),
    onSuccess: (data, variables) => {
      onSuccess(data.id, variables.administration_date, activePlayerId);
    },
    onError: (err) => {
      setConfirming(false);
      setError(err.response?.data?.error || 'An error occurred');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!activePlayerId || !respondentType || !asqType || !administrationDate) return;
    if (mutation.isPending) return;
    setError(null);
    setConfirming(true);
  };

  const handleConfirm = () => {
    const dateStr = moment(administrationDate).format('YYYY-MM-DD');
    mutation.mutate({
      respondent_type: respondentType,
      asq_type: asqType,
      administration_date: dateStr,
    });
  };

  const isValid = activePlayerId && respondentType && asqType && administrationDate;

  const asqTypeLabel = ASQ_TYPE_OPTIONS.find(o => o.value === asqType)?.label || asqType;

  return (
    <div className='column u-margin-top--base u-margin-bottom--base'>
      <h2 className='u-txt--base'><b>Add Completed ASQ</b></h2>
      <hr className='u-margin-top--zero' />
      <form onSubmit={handleSubmit}>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>Child</label>
          <Select
            name='player'
            className='o-select'
            clearable={false}
            disabled={!!preselectedPlayer}
            isLoading={isLoadingPlayers}
            value={selectedPlayer || ''}
            onChange={(opt) => {
              setSelectedPlayer(opt ? { value: opt.value, label: opt.label, birthday: opt.birthday, weeksPremature: opt.weeksPremature } : null);
              setAdministrationDate(null);
            }}
            onInputChange={onPlayerInputChange}
            filterOption={() => true}
            options={preselectedPlayer ? [preselectedPlayer] : playerOptions}
            placeholder='Select child...'
          />
          {/* 7a: show DOB and prematurity below child selector */}
          {playerBirthday && (
            <div style={{ fontSize: '0.82em', color: '#666', marginTop: 4 }}>
              DOB: {formatBirthday(playerBirthday)}
              {playerWeeksPremature > 0 && (
                <span style={{ marginLeft: 10 }}>· {playerWeeksPremature} weeks premature</span>
              )}
            </div>
          )}
        </div>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>Respondent</label>
          <Select
            name='respondentType'
            className='o-select'
            clearable={false}
            value={respondentType || ''}
            onChange={(opt) => setRespondentType(opt ? opt.value : null)}
            options={RESPONDENT_TYPE_OPTIONS}
          />
        </div>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>ASQ Type</label>
          <Select
            name='asqType'
            className='o-select'
            clearable={false}
            value={asqType || ''}
            onChange={(opt) => {
              setAsqType(opt ? opt.value : null);
              setAdministrationDate(null);
            }}
            options={ASQ_TYPE_OPTIONS}
            placeholder='Select ASQ type...'
          />
        </div>
        <div className='u-margin-bottom--base'>
          <label className='u-txt--base'>Administration Date</label>
          {/* 7c: show clear message when no valid date range exists */}
          {noDateRange ? (
            <div style={{ padding: '10px 12px', background: '#f8f8f8', border: '1px solid #ddd', borderRadius: 4, color: '#888', fontSize: '0.88em' }}>
              No valid date range available for this child and ASQ type. A completed ASQ cannot be added at this time.
            </div>
          ) : (
            <div>
              <DatePicker
                selected={administrationDate}
                onChange={(date) => {
                  setError(null);
                  setAdministrationDate(date);
                }}
                minDate={minDate}
                maxDate={maxDate}
                disabled={datePickerDisabled}
                placeholderText={datePickerDisabled ? 'Fill fields above first' : 'Select date...'}
                dateFormat='MM/dd/yyyy'
                className='o-input'
                popperPlacement='top'
                popperProps={{ strategy: 'fixed' }}
                style={{ width: '100%' }}
              />
            </div>
          )}
          {/* 7b: age range shown below date input as blue info box */}
          {ageRangeData && (
            ageRangeData.label ? (
              <div style={{ marginTop: 4, color: '#1a6fa8', fontSize: '0.88em', fontWeight: 'bold' }}>
                ASQ Screening for Selected Administration Date: {ageRangeData.label}
              </div>
            ) : (
              <div style={{ marginTop: 4, color: '#a06020', fontSize: '0.88em' }}>
                There is no available ASQ for that date.
              </div>
            )
          )}
          <p style={{ color: '#cc0000', fontSize: '0.85em', marginTop: '4px', marginBottom: 0, minHeight: '1.2em' }}>
            {error || ''}
          </p>
        </div>
        {/* 7d: updated confirmation warning with specific ASQ name */}
        {confirming && (
          <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '14px 16px', marginBottom: 16 }}>
            <p style={{ fontWeight: 'bold', marginBottom: 8 }}>⚠️ Please confirm before continuing</p>
            <p style={{ marginBottom: 0, fontSize: '0.9em', color: '#444' }}>
              If there is an active {ageRangeData?.label || asqTypeLabel} survey in progress, it will be <strong>archived</strong> and
              replaced by the new entry you are about to create. This action cannot be undone.
              Are you sure you want to continue?
            </p>
          </div>
        )}
        <div className='u-margin-top--base u-flex' style={{ justifyContent: 'flex-end', gap: 10 }}>
          {confirming ? (
            <>
              <button
                type='button'
                className='o-sp-button o-sp-button--base o-sp-button--secondary'
                onClick={() => setConfirming(false)}
                disabled={mutation.isPending}
              >
                Go Back
              </button>
              <button
                type='button'
                className='o-sp-button o-sp-button--base o-sp-button--primary'
                onClick={handleConfirm}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Processing...' : 'Yes, Continue'}
              </button>
            </>
          ) : (
            <button
              type='submit'
              className='o-sp-button o-sp-button--base o-sp-button--primary'
              disabled={!isValid || mutation.isPending || noDateRange}
            >
              Continue to ASQ Form
            </button>
          )}
        </div>
        <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12, fontSize: '0.82em', color: '#666', lineHeight: 1.5 }}>
          Sparkler calculates the correct screening based on the child&apos;s birthday and prematurity if they are
          under 2 years old. If you want to double-check the correct ASQ interval, you can use the{' '}
          <a href='https://agesandstages.com/resources-support/asq-calculator/' target='_blank' rel='noopener noreferrer'>ASQ Calculator</a>.
          If you need to update the child&apos;s birthday, administrators can update it in the administration tab.
          If you are a provider or teacher, please contact{' '}
          <a href='mailto:support@playsparkler.org'>support@playsparkler.org</a> to request this change.
        </div>
      </form>
    </div>
  );
};

AddCompletedASQModal.propTypes = {
  playerId: PropTypes.number,
  player: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddCompletedASQModal;
